// ReservaSection.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// Ajusta la ruta si tu SupabaseClient está en otra carpeta
import { supabase } from "../../Supabase/SupabaseClient";
import "./ReservaSection.css";
import Footer from "../../Components/Footer/Footer";

// Constante de modulo, no de componente: asi la referencia es estable y se
// puede poner en las deps del useEffect que la consume sin re-ejecutarlo en
// cada render (si viviera dentro del componente seria un array nuevo cada vez).
const availableSlots = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

export default function ReservaSection() {
  const initial = {
    nombre: "",
    email: "",
    telefono: "",
    servicio_id: "",
    dia: "",
    horario: "",
    tipo_sesion: "",
    preferencias: "",
    experiencia_anterior: "",
    presupuesto: "",
    presupuesto_otro: "",
    mensaje: "",
    estado: "",
    metodo_pago: "",
    direccion: "",
    observaciones: "",
  };

  const [form, setForm] = useState(initial);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
 const navigate = useNavigate();

  // UI states - por defecto usamos la lista de horarios
  const [useHorarioSelect, setUseHorarioSelect] = useState(true);
  const [showPresupuestoOtro, setShowPresupuestoOtro] = useState(false);

  // Horarios predefinidos (ejemplo) — puedes reemplazarlos o traerlos del servidor
  const prefsSuggestions = [
    "Horario flexible",
    "Prefiere profesional específico",
    "Necesita asesoramiento",
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  // Si estamos en modo lista de horarios, asigna por defecto la primera franja
  useEffect(() => {
    if (!useHorarioSelect) return;
    if (!availableSlots || availableSlots.length === 0) return;

    setForm((f) => {
      // si no hay horario o el horario actual no pertenece a availableSlots, asigna el primero
      if (!f.horario || !availableSlots.includes(f.horario)) {
        return { ...f, horario: availableSlots[0] };
      }
      return f;
    });
    // Ejecutar cuando se activa el modo lista o cuando cambia la fecha
  }, [useHorarioSelect, form.dia]);

  async function fetchServices() {
    try {
      const { data, error } = await supabase
        .from("servicios")
        .select("id, nombre")
        .order("id", { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      console.error("Error cargando servicios:", err);
      setStatusMsg({ type: "error", text: "No se pudieron cargar los servicios." });
    }
  }

  async function getUserId() {
    try {
      if (supabase.auth && typeof supabase.auth.getUser === "function") {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        return data?.user?.id || null;
      }
      if (supabase.auth && typeof supabase.auth.user === "function") {
        const user = supabase.auth.user();
        return user?.id || null;
      }
      if (supabase.auth && supabase.auth.session) {
        const s = supabase.auth.session();
        return s?.user?.id || null;
      }
    } catch (err) {
      console.warn("No se pudo obtener usuario (getUserId):", err);
    }
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));

    // comportamiento extra: si el usuario elige 'Otro' en presupuesto mostramos campo
    if (name === "presupuesto") {
      setShowPresupuestoOtro(value === "Otro");
      if (value !== "Otro") setForm((f) => ({ ...f, presupuesto_otro: "" }));
    }
  };

  const sanitizeTelefono = (tel) => {
    if (!tel) return null;
    const cleaned = tel.replace(/[^\d+]/g, "");
    return cleaned || null;
  };

  async function checkConflict(servicio_id, dia, horario) {
    try {
      const { data, error } = await supabase
        .from("reservas")
        .select("id, estado")
        .eq("servicio_id", Number(servicio_id))
        .eq("dia", dia)
        .eq("horario", horario)
        .neq("estado", "cancelado")
        .limit(1);

      if (error) throw error;
      return data && data.length > 0;
    } catch (err) {
      console.error("Error verificando conflicto:", err);
      throw err;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg(null);

    if (!form.nombre || !form.servicio_id || !form.dia || !form.horario) {
      setStatusMsg({ type: "error", text: "Completa nombre, servicio, día y horario." });
      return;
    }

    setLoading(true);

    try {
      const usuario_id = await getUserId();
      if (!usuario_id) {
        setLoading(false);
        setStatusMsg({
          type: "error",
          text: "No se encontró usuario autenticado. Asegúrate de iniciar sesión antes de reservar.",
        });
        return;
      }

      let exists = false;
      try {
        exists = await checkConflict(form.servicio_id, form.dia, form.horario);
      } catch (err) {
        setLoading(false);
        setStatusMsg({ type: "error", text: "No se pudo verificar la disponibilidad. Intenta más tarde." });
        return;
      }

      if (exists) {
        setLoading(false);
        setStatusMsg({ type: "error", text: "Ese horario ya está reservado. Elige otro." });
        return;
      }

      const payload = {
        usuario_id,
        servicio_id: Number(form.servicio_id),
        dia: form.dia,
        horario: form.horario,
        email: form.email || null,
        telefono: sanitizeTelefono(form.telefono),
        tipo_sesion: form.tipo_sesion || null,
        preferencias: form.preferencias || null,
        experiencia_anterior: form.experiencia_anterior || null,
        presupuesto: form.presupuesto === "Otro" ? form.presupuesto_otro || null : form.presupuesto || null,
        mensaje: form.mensaje || null,
        estado: form.estado || "pendiente",
        nombre: form.nombre || null,
        metodo_pago: form.metodo_pago || null,
        direccion: form.direccion || null,
        observaciones: form.observaciones || null,
      };

      const { error } = await supabase.from("reservas").insert([payload]);

      if (error) {
        console.error("Error insertando reserva:", error);
        throw error;
      }

      setStatusMsg({ type: "success", text: "Reserva creada correctamente. Completa los datos para el contrato y coordinemos la videollamada con Sole." });
      setForm(initial);
      setShowPresupuestoOtro(false);
      setUseHorarioSelect(true); // volvemos a modo lista por defecto
      setTimeout(() => {
        navigate("/contrato");
      }, 1600);
    } catch (err) {
      setStatusMsg({ type: "error", text: err?.message || "Ocurrió un error al crear la reserva." });
    } finally {
      setLoading(false);
    }
  };

  // Añade una sugerencia de preferencias al textarea, evitando duplicados
  const addPreference = (text) => {
    const current = form.preferencias ? form.preferencias.split(",").map((s) => s.trim()).filter(Boolean) : [];
    if (!current.includes(text)) {
      const updated = [...current, text].join(", ");
      setForm((f) => ({ ...f, preferencias: updated }));
    }
  };

  return (
    <>
    <section className="reserva-section">
      <div className="reserva-container">
        <div className="reserva-text">
          <span className="intro-line">Haz tu reserva</span>
          <h2 className="reserva-title">
            Agenda tu <span className="highlight">turno</span>
          </h2>
          <p className="reserva-description">Completa el formulario y asegura tu lugar en pocos segundos.</p>

          {statusMsg && (
            <div className={`reserva-alert ${statusMsg.type === "error" ? "error" : "success"}`}>
              {statusMsg.text}
            </div>
          )}
        </div>

        <form className="reserva-form" onSubmit={handleSubmit}>
          <input type="text" name="nombre" placeholder="Nombre y Apellido" value={form.nombre} onChange={handleChange} required />

          <select name="servicio_id" value={form.servicio_id} onChange={handleChange} required>
            <option value="">Selecciona un servicio</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre || s.NombreServicio}</option>
            ))}
          </select>

          <div className="row-two">
            <input type="date" name="dia" value={form.dia} onChange={handleChange} required />

            <div style={{ display: "flex", flexDirection: "column" }}>
             

              {useHorarioSelect ? (
                <select name="horario" value={form.horario} onChange={handleChange} required>
                  <option value="">Selecciona un horario</option>
                  {availableSlots.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              ) : (
                <input type="time" name="horario" value={form.horario} onChange={handleChange} required />
              )}
            </div>
          </div>

          <input type="email" name="email" placeholder="Correo electrónico (opcional)" value={form.email} onChange={handleChange} />

          <input type="tel" name="telefono" placeholder="Teléfono (opcional)" value={form.telefono} onChange={handleChange} />

          {/* tipo_sesion como select */}
          <select name="tipo_sesion" value={form.tipo_sesion} onChange={handleChange}>
            <option value="">Tipo de sesión (opcional)</option>
            <option value="Individual">Individual</option>
            <option value="Pareja">Pareja</option>
            <option value="Grupo">Grupo</option>
            <option value="Consulta">Consulta / Asesoramiento</option>
          </select>

          {/* presupuesto como select con opcion 'Otro' */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select name="presupuesto" value={form.presupuesto} onChange={handleChange}>
              <option value="">Presupuesto (opcional)</option>
              <option value="Económico">Económico</option>
              <option value="Estándar">Estándar</option>
              <option value="Premium">Premium</option>
              <option value="Otro">Otro...</option>
            </select>

            {showPresupuestoOtro && (
              <input name="presupuesto_otro" value={form.presupuesto_otro} onChange={handleChange} placeholder="Describe tu presupuesto" />
            )}
          </div>

          {/* experiencia_anterior como select */}
          <select name="experiencia_anterior" value={form.experiencia_anterior} onChange={handleChange}>
            <option value="">Experiencia anterior (opcional)</option>
            <option value="Nunca">Nunca</option>
            <option value="Algunas veces">Algunas veces</option>
            <option value="Frecuente">Frecuente</option>
            <option value="Profesional">Profesional / Muy experimentado</option>
          </select>

          {/* preferencias: textarea + sugerencias rápidas */}
          <div>
            <div style={{ marginBottom: 8 }}>
              {prefsSuggestions.map((p) => (
                <button
                  type="button"
                  key={p}
                  className="pref-chip"
                  onClick={() => addPreference(p)}
                >
                  + {p}
                </button>
              ))}
            </div>
            <textarea name="preferencias" placeholder="Preferencias (opcional) — usa las sugerencias rápidas o escribe las tuyas" value={form.preferencias} onChange={handleChange} rows={3} />
          </div>

          <textarea name="mensaje" placeholder="Mensaje adicional (opcional)" value={form.mensaje} onChange={handleChange} rows={3} />

          <select name="metodo_pago" value={form.metodo_pago} onChange={handleChange}>
            <option value="">Método de pago (opcional)</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Tarjeta">Tarjeta</option>
            <option value="Transferencia">Transferencia bancaria</option>
            <option value="MercadoPago">Mercado Pago</option>
          </select>

          <input type="text" name="direccion" placeholder="Dirección (opcional)" value={form.direccion} onChange={handleChange} />

          <textarea name="observaciones" placeholder="Observaciones (opcional)" value={form.observaciones} onChange={handleChange} rows={2} />

          <div className="form-actions">
            <button type="submit" className="reserva-btn" disabled={loading}>
              {loading ? "Reservando..." : "Reservar ahora"}
            </button>
          </div>
        </form>
      </div>
    </section>
      <Footer />
    </>
  );
}
